import React from 'react';
import { Label, Button } from 'fw/components';
import { DocumentUpload } from 'areas/shared/controls';
import APICall from 'fw/system/API';
import * as Formatting from 'fw/misc/Formatting';
import * as ReportManager from 'modules/reports/ReportManager';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';

// NVBDC-1404 — declaration upload, revenue/app-fee display.
class FinancialsSection extends React.Component {
    onDeclarationUploadChange = (file) => {
        this.props.onDeclarationChange({
            ...this.props.declaration,
            declarationStorageToken: file.storageToken,
            declarationFileName: file.fileName
        });
    };

    // Same download pattern as the classic wizard's own Declaration step
    // (areas/certification/steps/declaration/Declaration.js:43-48) — the blank template is a
    // fixed app-config file (DeclarationFormStorageKey), not something the one-sheet uploads.
    downloadDeclarationForm = () => {
        new APICall().download({
            storageToken: this.props.declaration && this.props.declaration.declarationFormStorageToken,
            fileName: this.props.declaration && this.props.declaration.declarationFormFileName
        });
    };

    formatCurrency = (amount) => {
        if (amount === null || amount === undefined || amount === '') return '—';
        return '$' + Number(amount).toLocaleString('en-US');
    };

    // Mockup labels these "*2026 YTD Gross Revenue"/"*2025 Gross Revenue" — the actual year, not
    // generic "YTD"/"Prior Year" wording. grossRevenue1Year/grossRevenue2Year already exist on
    // vobInfo (same fields the classic wizard's CompanyInfo.js shows, read-only, "From Prequal").
    revenueLabel = (year, fallback) => (year ? `${year} ${fallback}` : fallback);

    // vobInfo is a prop here (not local state), so every change round-trips through
    // OneSheetRenewal's onVobInfoChange choke point — same array-mutation pattern as
    // CompanySection.js's phone editor.
    //
    // GrossRevenueDocumentModel (backend) has no "which year" tag — it's one flat list per VOB,
    // same as the classic wizard's CompanyInfo.js:254-300 "Gross Revenue Supporting Documents".
    // An earlier version of this section put a "Select File" control directly on each year's row
    // (array position 0/1 standing in for "this year"/"prior year"), matching the mockup's layout —
    // reverted per GAP-1404-04: since there's no real per-year link in the data, that implied a
    // relationship that doesn't exist. Matches the classic wizard's own flat-list model instead:
    // one shared list, add/remove, no positional meaning.
    handleAddGrossRevenueDocument = () => {
        const vobInfo = this.props.vobInfo;
        const grossRevenueDocuments = [...(vobInfo.grossRevenueDocuments || [])];
        grossRevenueDocuments.push({ vobGrossRevenueDocumentId: -Math.abs(Date.now() | 0) });
        this.props.onVobInfoChange({ ...vobInfo, grossRevenueDocuments });
    };

    handleRemoveGrossRevenueDocument = (index) => {
        const vobInfo = this.props.vobInfo;
        const grossRevenueDocuments = [...(vobInfo.grossRevenueDocuments || [])];
        grossRevenueDocuments.splice(index, 1);
        this.props.onVobInfoChange({ ...vobInfo, grossRevenueDocuments });
    };

    handleGrossRevenueDocumentChange = (file, index) => {
        const vobInfo = this.props.vobInfo;
        const grossRevenueDocuments = [...(vobInfo.grossRevenueDocuments || [])];
        grossRevenueDocuments[index] = { ...grossRevenueDocuments[index], storageToken: file.storageToken, fileName: file.fileName };
        this.props.onVobInfoChange({ ...vobInfo, grossRevenueDocuments });
    };

    renderGrossRevenueDocuments = () => {
        const grossRevenueDocuments = (this.props.vobInfo && this.props.vobInfo.grossRevenueDocuments) || [];

        return (
            <div className="one-sheet-financials-row-stacked">
                <Label htmlFor="grossRevenueDocumentUpload">Gross Revenue Supporting Documents</Label>
                {grossRevenueDocuments.map((item, i) => (
                    <div key={item.vobGrossRevenueDocumentId} className="one-sheet-row-controls">
                        <DocumentUpload
                            storageToken={item.storageToken}
                            fileName={item.fileName}
                            onChange={file => this.handleGrossRevenueDocumentChange(file, i)}
                        />
                        <Button
                            id={`btnRemoveGrossRevenueDocument-${i}`}
                            bsStyle="link"
                            className="one-sheet-row-remove-btn"
                            title="Remove Gross Revenue Supporting Document"
                            onClick={() => this.handleRemoveGrossRevenueDocument(i)}>
                            <FontAwesomeIcon icon={faTrashAlt} />
                        </Button>
                    </div>
                ))}
                {grossRevenueDocuments.length < 5 &&
                    <Button bsStyle="default" bsSize="xsmall" onClick={this.handleAddGrossRevenueDocument}>
                        Add Additional Supporting Document
                    </Button>
                }
            </div>
        );
    };

    // Same report request the classic wizard's Company Info step builds for its "Payment" popup
    // (areas/certification/steps/companyInfo/OnlinePaymentInfoPopUp.js:35-48) — the mockup's
    // "Receipt" link next to the App Fee is this same PaymentInfoReport, not a new report.
    downloadReceipt = () => {
        const onlinePayment = this.props.vobInfo && this.props.vobInfo.onlinePayment;
        if (!onlinePayment) return;

        ReportManager.execute({
            reportType: 'PaymentInfoReport',
            format: 'PDF',
            parameters: {
                CertificationId: this.props.certificationId,
                PaymentDate: Formatting.formatDateTime(onlinePayment.paymentDate),
                PaymentAmount: Formatting.formatCurrency(onlinePayment.paymentAmount)
            }
        });
    };

    render() {
        const { vobInfo, declaration } = this.props;

        return (
            <div>
                <div className="one-sheet-financials-row">
                    <Label htmlFor="declarationFormStorageToken" required>Declaration Blank Form</Label>
                    <Button id="btnDownloadDeclarationForm" bsStyle="link" onClick={this.downloadDeclarationForm}>Download Template</Button>
                </div>
                <div className="one-sheet-financials-row-stacked">
                    <Label htmlFor="declarationStorageToken" required>Notarized Declaration</Label>
                    <DocumentUpload
                        storageToken={declaration && declaration.declarationStorageToken}
                        fileName={declaration && declaration.declarationFileName}
                        onChange={this.onDeclarationUploadChange}
                    />
                </div>
                <div className="one-sheet-financials-row">
                    <Label>{this.revenueLabel(vobInfo && vobInfo.grossRevenue1Year, 'YTD Gross Revenue')}</Label>
                    <span>{this.formatCurrency(vobInfo && vobInfo.grossRevenue1Amount)}</span>
                </div>
                <div className="one-sheet-financials-row">
                    <Label>{this.revenueLabel(vobInfo && vobInfo.grossRevenue2Year, 'Prior Year Gross Revenue')}</Label>
                    <span>{this.formatCurrency(vobInfo && vobInfo.grossRevenue2Amount)}</span>
                </div>
                {this.renderGrossRevenueDocuments()}
                <div className="one-sheet-financials-row">
                    <Label>App Fee</Label>
                    <span>{this.formatCurrency(vobInfo && vobInfo.applicationFee)}</span>
                    {vobInfo && vobInfo.onlinePayment && (
                        <Button id="btnDownloadReceipt" bsStyle="link" onClick={this.downloadReceipt}>Receipt</Button>
                    )}
                </div>
            </div>
        );
    }
}

export default FinancialsSection;



// WEBPACK FOOTER //
// ./src/areas/certification/oneSheet/sections/FinancialsSection.js